/**
 * Tenant Management — Super Admin only.
 *
 * Unified Tenant + Location view: each tenant is a parent org. Click to expand
 * and see locations under that tenant + edit cap, plan, status. Replaces the
 * standalone Location Management as the top-level admin view (LM still works
 * but Tenants is now the primary surface).
 */
import { useEffect, useState, useCallback, Fragment } from "react";import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  ShieldCheck, Plus, ChevronRight, ChevronDown, MapPin, Eye, Loader2,
  AlertCircle, Building2, RefreshCw, Crown,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { UnassignedLocations } from "@/components/tenants/UnassignedLocations";
import { TenantUsersPanel } from "@/components/tenants/TenantUsersPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLAN_OPTIONS = [
  { value: "free", label: "Free" },
  { value: "trial", label: "Trial" },
  { value: "paid", label: "Paid" },
];
const STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "suspended", label: "Suspended" },
  { value: "archived", label: "Archived" },
];

function planBadge(plan) {
  const map = {
    free: { c: "bg-slate-100 text-slate-700", l: "Free" },
    trial: { c: "bg-amber-100 text-amber-700", l: "Trial" },
    paid: { c: "bg-emerald-100 text-emerald-700", l: "Paid" },
  };
  const v = map[plan] || map.free;
  return <Badge className={v.c}>{v.l}</Badge>;
}

function statusBadge(status) {
  const map = {
    active: { c: "bg-emerald-100 text-emerald-700", l: "Active" },
    suspended: { c: "bg-amber-100 text-amber-700", l: "Suspended" },
    archived: { c: "bg-slate-100 text-slate-500", l: "Archived" },
  };
  const v = map[status] || map.active;
  return <Badge className={v.c}>{v.l}</Badge>;
}

export default function TenantManagement() {
  const navigate = useNavigate();
  const [tenants, setTenants] = useState([]);
  const [tenantDetails, setTenantDetails] = useState({}); // id → {locations: [...]}
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [expanded, setExpanded] = useState(new Set());

  // Edit-cap inline state
  const [editingCap, setEditingCap] = useState(null);
  const [capValue, setCapValue] = useState(1);

  // Edit dialog state
  const [editingTenant, setEditingTenant] = useState(null);
  const [editForm, setEditForm] = useState({});

  // Create dialog state
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({
    name: "", owner_email: "", owner_name: "", plan: "free", max_locations: 1,
  });
  const [creating, setCreating] = useState(false);

  // Permission check (super_admin only)
  const cachedUser = JSON.parse(localStorage.getItem("cachedUser") || "{}");
  const isSuperAdmin = cachedUser.role === "super_admin";

  const fetchTenants = useCallback(async () => {
    try {
      setRefreshing(true);
      const res = await axios.get(`${API}/admin/tenants`);
      setTenants(res.data.tenants || []);
    } catch (err) {
      console.error("Failed to load tenants", err);
      toast.error(err.response?.data?.detail || "Failed to load tenants");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    if (!isSuperAdmin) {
      navigate("/ops");
      return;
    }
    fetchTenants();
  }, [fetchTenants, isSuperAdmin, navigate]);

  const refreshTenantDetail = async (tenantId) => {
    try {
      const res = await axios.get(`${API}/admin/tenants/${tenantId}`);
      setTenantDetails(prev => ({ ...prev, [tenantId]: res.data }));
    } catch (err) {
      console.error("Failed to reload tenant detail", err);
    }
  };

  const toggleExpand = async (tenantId) => {
    const next = new Set(expanded);
    if (next.has(tenantId)) {
      next.delete(tenantId);
    } else {
      next.add(tenantId);
      // Lazy-load locations on first expand
      if (!tenantDetails[tenantId]) {
        try {
          const res = await axios.get(`${API}/admin/tenants/${tenantId}`);
          setTenantDetails(prev => ({ ...prev, [tenantId]: res.data }));
        } catch (err) {
          toast.error("Failed to load tenant locations");
        }
      }
    }
    setExpanded(next);
  };

  const startEditCap = (t, e) => {
    e?.stopPropagation();
    setEditingCap(t.id);
    setCapValue(t.max_locations);
  };

  const saveCap = async (id, e) => {
    e?.stopPropagation();
    try {
      await axios.put(`${API}/admin/tenants/${id}`, { max_locations: capValue });
      toast.success("Location cap updated");
      setEditingCap(null);
      // Refresh both the parent list AND the cached drill-down detail (if expanded)
      // otherwise the panel keeps showing stale at_cap based on old max_locations.
      await Promise.all([
        fetchTenants(),
        expanded.has(id) ? refreshTenantDetail(id) : Promise.resolve(),
      ]);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update cap");
    }
  };

  const openEditDialog = (t, e) => {
    e?.stopPropagation();
    setEditingTenant(t);
    setEditForm({
      name: t.name,
      owner_email: t.owner_email || "",
      owner_name: t.owner_name || "",
      plan: t.plan,
      status: t.status,
      max_locations: t.max_locations,
      notes: t.notes || "",
    });
  };

  const saveEdit = async () => {
    try {
      await axios.put(`${API}/admin/tenants/${editingTenant.id}`, editForm);
      toast.success("Tenant updated");
      const tid = editingTenant.id;
      setEditingTenant(null);
      // Also refresh the drill-down cache so cap/plan/status changes reflect immediately
      await Promise.all([
        fetchTenants(),
        expanded.has(tid) ? refreshTenantDetail(tid) : Promise.resolve(),
      ]);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update tenant");
    }
  };

  const submitCreate = async () => {
    if (!createForm.name.trim()) {
      toast.error("Tenant name is required");
      return;
    }
    setCreating(true);
    try {
      await axios.post(`${API}/admin/tenants`, createForm);
      toast.success(`Tenant '${createForm.name}' created`);
      setCreateOpen(false);
      setCreateForm({ name: "", owner_email: "", owner_name: "", plan: "free", max_locations: 1 });
      fetchTenants();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create tenant");
    } finally {
      setCreating(false);
    }
  };

  if (!isSuperAdmin) return null;

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="p-4 lg:p-6 max-w-7xl mx-auto" data-testid="tenant-management-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <ShieldCheck className="h-6 w-6 text-indigo-600" /> Tenant Management
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Super-admin only · Tenant orgs and the locations they own. Click any tenant to drill in.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={fetchTenants}
            disabled={refreshing}
            data-testid="refresh-tenants-btn"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? "animate-spin" : ""}`} /> Refresh
          </Button>
          <Button
            className="bg-indigo-600 hover:bg-indigo-700"
            onClick={() => setCreateOpen(true)}
            data-testid="create-tenant-btn"
          >
            <Plus className="h-4 w-4 mr-2" /> Create New Tenant
          </Button>
        </div>
      </div>

      <UnassignedLocations tenants={tenants} onChanged={fetchTenants} reloadKey={refreshing} />

      {/* Tenant table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr className="text-xs uppercase tracking-wider text-gray-500">
                  <th className="text-left px-4 py-3 font-semibold w-8"></th>
                  <th className="text-left px-4 py-3 font-semibold">Tenant</th>
                  <th className="text-left px-4 py-3 font-semibold">Owner</th>
                  <th className="text-center px-4 py-3 font-semibold">Plan</th>
                  <th className="text-center px-4 py-3 font-semibold">Status</th>
                  <th className="text-center px-4 py-3 font-semibold">Locations</th>
                  <th className="text-center px-4 py-3 font-semibold">Cap</th>
                  <th className="text-right px-4 py-3 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {tenants.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-4 py-8 text-center text-gray-500">
                      No tenants yet. Create one to get started.
                    </td>
                  </tr>
                )}
                {tenants.map(t => {
                  const isExp = expanded.has(t.id);
                  const detail = tenantDetails[t.id];
                  return (
                    <Fragment key={t.id}>
                      <tr
                        className="border-b hover:bg-indigo-50 cursor-pointer transition-colors"
                        onClick={() => toggleExpand(t.id)}
                        data-testid={`tenant-row-${t.id}`}
                      >
                        <td className="px-4 py-3">
                          {isExp
                            ? <ChevronDown className="h-4 w-4 text-indigo-600" />
                            : <ChevronRight className="h-4 w-4 text-gray-400" />}
                        </td>
                        <td className="px-4 py-3">
                          <div className="font-semibold text-gray-900 flex items-center gap-2">
                            <Crown className="h-3.5 w-3.5 text-indigo-500" /> {t.name}
                          </div>
                          <div className="text-xs text-gray-500 mt-0.5">{t.id}</div>
                        </td>
                        <td className="px-4 py-3">
                          {t.owner_email ? (
                            <div>
                              <div className="text-gray-700">{t.owner_email}</div>
                              {t.owner_name && <div className="text-xs text-gray-500">{t.owner_name}</div>}
                            </div>
                          ) : (
                            <span className="text-xs text-gray-400 italic">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center">{planBadge(t.plan)}</td>
                        <td className="px-4 py-3 text-center">{statusBadge(t.status)}</td>
                        <td className="px-4 py-3 text-center font-semibold">
                          <span className={t.at_cap ? "text-amber-600" : "text-gray-700"}>
                            {t.location_count} / {t.max_locations}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          {editingCap === t.id ? (
                            <div className="inline-flex items-center gap-2" onClick={e => e.stopPropagation()}>
                              <Input
                                type="number"
                                min={1}
                                max={50}
                                value={capValue}
                                onChange={e => setCapValue(parseInt(e.target.value) || 1)}
                                className="w-16 h-7 text-center"
                                data-testid={`cap-input-${t.id}`}
                              />
                              <Button
                                size="sm"
                                onClick={e => saveCap(t.id, e)}
                                className="h-7 px-2 bg-emerald-600 hover:bg-emerald-700"
                                data-testid={`save-cap-${t.id}`}
                              >
                                Save
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={e => { e.stopPropagation(); setEditingCap(null); }}
                                className="h-7 px-2"
                              >
                                ✕
                              </Button>
                            </div>
                          ) : (
                            <span className="font-semibold text-gray-700">{t.max_locations}</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="inline-flex gap-1" onClick={e => e.stopPropagation()}>
                            {editingCap !== t.id && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={e => startEditCap(t, e)}
                                className="h-7 text-xs"
                                data-testid={`edit-cap-${t.id}`}
                              >
                                Edit Cap
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={e => openEditDialog(t, e)}
                              className="h-7 text-xs"
                              data-testid={`edit-tenant-${t.id}`}
                            >
                              Edit
                            </Button>
                          </div>
                        </td>
                      </tr>
                      {isExp && (
                        <tr className="bg-slate-50 border-b">
                          <td colSpan={8} className="px-6 py-4">
                            {!detail ? (
                              <div className="flex items-center gap-2 text-sm text-gray-500">
                                <Loader2 className="h-4 w-4 animate-spin" /> Loading locations…
                              </div>
                            ) : (
                              <>
                              <TenantLocationsPanel
                                detail={detail}
                                onChanged={async () => {
                                  await refreshTenantDetail(t.id);
                                  fetchTenants();
                                }}
                              />
                              <TenantUsersPanel detail={detail} onChanged={() => refreshTenantDetail(t.id)} />
                              </>
                            )}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-blue-50 border-blue-100 mt-4">
        <CardContent className="p-4 flex items-start gap-3">
          <AlertCircle className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
          <div className="text-xs text-gray-700">
            <strong className="text-gray-900">How this works:</strong>{" "}
            You manually create the <strong>Tenant + first location</strong> for each customer (security gate).
            Set their <strong>max_locations cap</strong> based on contract. Tenant admins will eventually
            self-add additional locations under their own Gingr key — capped at this number.
            For now, expand a tenant and use the existing
            <a href="/location-management" className="text-indigo-600 underline mx-1">Location Management</a>
            page to add or edit locations directly.
          </div>
        </CardContent>
      </Card>

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-md" data-testid="create-tenant-dialog">
          <DialogHeader>
            <DialogTitle>Create New Tenant</DialogTitle>
            <DialogDescription>
              A tenant is a customer org that owns one or more locations.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs">Tenant Name *</Label>
              <Input
                value={createForm.name}
                onChange={e => setCreateForm(f => ({ ...f, name: e.target.value }))}
                placeholder="e.g. Garden City Pet Resorts LLC"
                data-testid="new-tenant-name"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Owner Email</Label>
                <Input
                  type="email"
                  value={createForm.owner_email}
                  onChange={e => setCreateForm(f => ({ ...f, owner_email: e.target.value }))}
                  placeholder="glen@example.com"
                  data-testid="new-tenant-email"
                />
              </div>
              <div>
                <Label className="text-xs">Owner Name</Label>
                <Input
                  value={createForm.owner_name}
                  onChange={e => setCreateForm(f => ({ ...f, owner_name: e.target.value }))}
                  placeholder="Glen Roberts"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Plan</Label>
                <Select value={createForm.plan} onValueChange={v => setCreateForm(f => ({ ...f, plan: v }))}>
                  <SelectTrigger data-testid="new-tenant-plan"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PLAN_OPTIONS.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Max Locations</Label>
                <Input
                  type="number"
                  min={1}
                  max={50}
                  value={createForm.max_locations}
                  onChange={e => setCreateForm(f => ({ ...f, max_locations: parseInt(e.target.value) || 1 }))}
                  data-testid="new-tenant-cap"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)} disabled={creating}>Cancel</Button>
            <Button
              onClick={submitCreate}
              disabled={creating || !createForm.name.trim()}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="submit-create-tenant"
            >
              {creating ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Creating…</> : "Create Tenant"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={!!editingTenant} onOpenChange={open => !open && setEditingTenant(null)}>
        <DialogContent className="max-w-md" data-testid="edit-tenant-dialog">
          <DialogHeader>
            <DialogTitle>Edit Tenant</DialogTitle>
            <DialogDescription>{editingTenant?.name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs">Tenant Name</Label>
              <Input
                value={editForm.name || ""}
                onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Owner Email</Label>
                <Input
                  type="email"
                  value={editForm.owner_email || ""}
                  onChange={e => setEditForm(f => ({ ...f, owner_email: e.target.value }))}
                />
              </div>
              <div>
                <Label className="text-xs">Owner Name</Label>
                <Input
                  value={editForm.owner_name || ""}
                  onChange={e => setEditForm(f => ({ ...f, owner_name: e.target.value }))}
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Plan</Label>
                <Select value={editForm.plan || "free"} onValueChange={v => setEditForm(f => ({ ...f, plan: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PLAN_OPTIONS.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Status</Label>
                <Select value={editForm.status || "active"} onValueChange={v => setEditForm(f => ({ ...f, status: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Cap</Label>
                <Input
                  type="number"
                  min={1}
                  max={50}
                  value={editForm.max_locations || 1}
                  onChange={e => setEditForm(f => ({ ...f, max_locations: parseInt(e.target.value) || 1 }))}
                />
              </div>
            </div>
            <div>
              <Label className="text-xs">Notes</Label>
              <Input
                value={editForm.notes || ""}
                onChange={e => setEditForm(f => ({ ...f, notes: e.target.value }))}
                placeholder="Internal notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingTenant(null)}>Cancel</Button>
            <Button onClick={saveEdit} className="bg-indigo-600 hover:bg-indigo-700" data-testid="submit-edit-tenant">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ── Locations panel inside tenant drill-down ─────────────────────────────
function TenantLocationsPanel({ detail, onChanged }) {
  const { id: tenantId, locations = [], max_locations = 1, name: tenantName } = detail;
  const remaining = Math.max(0, max_locations - locations.length);
  const atCap = remaining === 0;

  const [addOpen, setAddOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [inheritable, setInheritable] = useState(null); // {available, app_url, api_key_masked, ...}
  const [partitions, setPartitions] = useState(null); // Gingr discovery result
  const [loadingPartitions, setLoadingPartitions] = useState(false);
  const [form, setForm] = useState({
    name: "", address: "", city: "", state: "", zip_code: "",
    phone: "", timezone: "America/New_York",
    gingr_location_id: "", gingr_location_name: "",
    // Optional initial-admin block (mirrors the legacy "Onboard Location" UX)
    create_admin: false,
    admin_first_name: "",
    admin_last_name: "",
    admin_email: "",
    admin_password: "",
    admin_role: "admin",
    send_welcome_email: true,
  });

  // When dialog opens, check if tenant has inheritable Gingr from a sibling
  // AND auto-discover available Gingr partitions from the live API
  useEffect(() => {
    if (!addOpen) return;
    setInheritable(null);
    setPartitions(null);
    axios
      .get(`${API}/admin/tenants/${tenantId}/inheritable-gingr`)
      .then(res => {
        setInheritable(res.data);
        // If Gingr is configured, also run discovery
        if (res.data?.available) {
          setLoadingPartitions(true);
          axios
            .get(`${API}/admin/tenants/${tenantId}/gingr-partitions`)
            .then(pres => setPartitions(pres.data))
            .catch(() => setPartitions({ success: false, error: "Discovery failed" }))
            .finally(() => setLoadingPartitions(false));
        }
      })
      .catch(() => setInheritable({ available: false }));
  }, [addOpen, tenantId]);

  // When user picks a partition from dropdown, auto-fill name fields
  const handlePartitionPick = (pid) => {
    const p = partitions?.partitions?.find(pp => String(pp.gingr_id) === String(pid));
    setForm(f => ({
      ...f,
      gingr_location_id: pid,
      gingr_location_name: p?.name || "",
      // If the location Name field is empty, pre-fill with Gingr name for convenience
      name: f.name.trim() === "" && p?.name ? p.name : f.name,
    }));
  };

  const submitAdd = async () => {
    if (!form.name.trim()) {
      toast.error("Location name is required");
      return;
    }
    if (inheritable?.available && !form.gingr_location_id) {
      toast.error("Gingr partition ID is required when inheriting from sibling");
      return;
    }
    // Validate admin block if enabled
    if (form.create_admin) {
      if (!form.admin_first_name.trim() || !form.admin_last_name.trim()) {
        toast.error("Admin first & last name are required");
        return;
      }
      if (!form.admin_email.trim()) {
        toast.error("Admin email is required");
        return;
      }
      if ((form.admin_password || "").length < 8) {
        toast.error("Admin password must be at least 8 characters");
        return;
      }
    }
    setAdding(true);
    try {
      const body = {
        name: form.name,
        address: form.address || null,
        city: form.city || null,
        state: form.state || null,
        zip_code: form.zip_code || null,
        phone: form.phone || null,
        timezone: form.timezone,
      };
      if (inheritable?.available && form.gingr_location_id) {
        body.gingr_location_id = parseInt(form.gingr_location_id, 10);
        body.gingr_location_name = form.gingr_location_name || form.name;
      }
      const res = await axios.post(`${API}/admin/tenants/${tenantId}/locations`, body);
      toast.success(res.data.message);
      if (res.data.gingr_inherited) {
        toast.success("Gingr API inherited — initial sync will start in the background", { duration: 6000 });
      }

      // Optionally create the initial admin and send welcome email
      if (form.create_admin && res.data?.location?.id) {
        try {
          const userRes = await axios.post(`${API}/admin/users/create`, {
            email: form.admin_email.trim(),
            password: form.admin_password,
            first_name: form.admin_first_name.trim(),
            last_name: form.admin_last_name.trim(),
            role: form.admin_role,
            location_id: res.data.location.id,
            send_welcome_email: !!form.send_welcome_email,
          });
          if (userRes.data?.welcome_email_sent) {
            toast.success(`Welcome email sent to ${form.admin_email}`, { duration: 6000 });
          } else if (form.send_welcome_email) {
            toast.warning("Admin created, but welcome email failed to send — check logs");
          } else {
            toast.success(`Admin user ${form.admin_email} created`);
          }
        } catch (uerr) {
          toast.error(
            `Location created, but admin creation failed: ${
              uerr.response?.data?.detail || "unknown error"
            }`
          );
        }
      }

      setAddOpen(false);
      setForm({
        name: "", address: "", city: "", state: "", zip_code: "",
        phone: "", timezone: "America/New_York",
        gingr_location_id: "", gingr_location_name: "",
        create_admin: false,
        admin_first_name: "",
        admin_last_name: "",
        admin_email: "",
        admin_password: "",
        admin_role: "admin",
        send_welcome_email: true,
      });
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add location");
    } finally {
      setAdding(false);
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
        <div className="text-xs uppercase tracking-wider text-gray-500 font-semibold flex items-center gap-2">
          <MapPin className="h-3.5 w-3.5" /> Locations under this tenant ({locations.length} of {max_locations})
        </div>
        <Button
          size="sm"
          onClick={() => setAddOpen(true)}
          disabled={atCap}
          className="bg-indigo-600 hover:bg-indigo-700 h-7 text-xs"
          data-testid={`add-location-${tenantId}`}
        >
          <Plus className="h-3.5 w-3.5 mr-1" /> Add Location
        </Button>
      </div>
      {atCap && (
        <div className="text-xs rounded border border-amber-200 bg-amber-50 p-2 text-amber-900">
          At cap. Increase <strong>max_locations</strong> on this tenant before adding more.
        </div>
      )}
      {locations.length === 0 ? (
        <div className="text-sm text-gray-500 italic py-2">No locations assigned yet.</div>
      ) : (
        locations.map(loc => (
          <LocationCardRow
            key={loc.id}
            loc={loc}
            tenantId={tenantId}
            onChanged={onChanged}
          />
        ))
      )}

      {/* Add Location Dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="add-location-dialog">
          <DialogHeader>
            <DialogTitle>Add Location to {tenantName}</DialogTitle>
            <DialogDescription>
              Creates a new location and links it to this tenant.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs">Location Name *</Label>
              <Input
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="e.g. K9 Resorts of Syosset"
                data-testid="add-loc-name"
              />
            </div>
            <div>
              <Label className="text-xs">Address</Label>
              <Input
                value={form.address}
                onChange={e => setForm(f => ({ ...f, address: e.target.value }))}
                placeholder="123 Main St"
              />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">City</Label>
                <Input
                  value={form.city}
                  onChange={e => setForm(f => ({ ...f, city: e.target.value }))}
                />
              </div>
              <div>
                <Label className="text-xs">State</Label>
                <Input
                  value={form.state}
                  onChange={e => setForm(f => ({ ...f, state: e.target.value }))}
                  placeholder="NY"
                  maxLength={2}
                />
              </div>
              <div>
                <Label className="text-xs">Zip</Label>
                <Input
                  value={form.zip_code}
                  onChange={e => setForm(f => ({ ...f, zip_code: e.target.value }))}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Phone</Label>
                <Input
                  value={form.phone}
                  onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                />
              </div>
              <div>
                <Label className="text-xs">Timezone</Label>
                <Select
                  value={form.timezone}
                  onValueChange={v => setForm(f => ({ ...f, timezone: v }))}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="America/New_York">America/New_York</SelectItem>
                    <SelectItem value="America/Chicago">America/Chicago</SelectItem>
                    <SelectItem value="America/Denver">America/Denver</SelectItem>
                    <SelectItem value="America/Los_Angeles">America/Los_Angeles</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Inheritable Gingr block */}
            {inheritable === null && (
              <div className="text-xs text-gray-400 italic py-2">
                <Loader2 className="h-3 w-3 animate-spin inline mr-1" /> Checking for inheritable Gingr config…
              </div>
            )}
            {inheritable?.available && (
              <div
                className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 space-y-3"
                data-testid="inherit-gingr-block"
              >
                <div className="text-sm font-semibold text-emerald-900 flex items-center gap-2">
                  <ShieldCheck className="h-4 w-4 text-emerald-600" />
                  Inherit Gingr API from <strong>{inheritable.source_location_name}</strong>
                </div>
                <div className="text-xs text-emerald-800 grid grid-cols-2 gap-2">
                  <div>
                    <span className="text-gray-500">Subdomain</span>
                    <div className="font-mono text-gray-900 font-semibold">
                      {inheritable.app_url}
                    </div>
                  </div>
                  <div>
                    <span className="text-gray-500">API Key</span>
                    <div className="font-mono text-gray-900 font-semibold">
                      {inheritable.api_key_masked}
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Gingr Partition *</Label>
                    {loadingPartitions ? (
                      <div className="h-10 flex items-center text-xs text-gray-500">
                        <Loader2 className="h-3 w-3 animate-spin mr-2" /> Discovering partitions…
                      </div>
                    ) : partitions?.success && partitions.partitions.length > 0 ? (
                      <Select
                        value={String(form.gingr_location_id)}
                        onValueChange={handlePartitionPick}
                      >
                        <SelectTrigger data-testid="add-loc-gingr-partition-select">
                          <SelectValue placeholder="Select partition…" />
                        </SelectTrigger>
                        <SelectContent>
                          {partitions.partitions.map(p => (
                            <SelectItem
                              key={p.gingr_id}
                              value={String(p.gingr_id)}
                              disabled={p.already_added}
                            >
                              #{p.gingr_id} · {p.name}{p.already_added && " (already added)"}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input
                        type="number"
                        min={1}
                        value={form.gingr_location_id}
                        onChange={e => setForm(f => ({ ...f, gingr_location_id: e.target.value }))}
                        placeholder="e.g. 2"
                        data-testid="add-loc-gingr-partition"
                      />
                    )}
                    <div className="text-[10px] text-gray-500 mt-0.5">
                      {partitions?.success
                        ? `✓ Auto-discovered ${partitions.partitions.length} partition${partitions.partitions.length !== 1 ? "s" : ""} from Gingr`
                        : inheritable.source_gingr_location_id != null
                          ? `Sibling uses #${inheritable.source_gingr_location_id}`
                          : "Integer partition ID assigned by Gingr"}
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">Gingr Location Name</Label>
                    <Input
                      value={form.gingr_location_name}
                      onChange={e => setForm(f => ({ ...f, gingr_location_name: e.target.value }))}
                      placeholder="(auto-fills from picker)"
                    />
                  </div>
                </div>
                <div className="text-[11px] text-emerald-700 leading-snug">
                  ✓ On save, we'll auto-create a Gingr integration row for this location using the same key.
                  Initial data sync starts within 5 min via the background scheduler.
                </div>
              </div>
            )}
            {inheritable && !inheritable.available && (
              <div className="text-xs text-gray-500 rounded border border-gray-200 bg-gray-50 p-2">
                No sibling location has Gingr configured yet. After creating this location,
                wire up Gingr from
                <a href="/location-management" className="text-indigo-600 underline mx-1">Location Management</a>
                manually.
              </div>
            )}

            {/* ── Optional: create initial admin + welcome email ───────── */}
            <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-3 space-y-3">
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!form.create_admin}
                  onChange={(e) => setForm(f => ({ ...f, create_admin: e.target.checked }))}
                  className="mt-0.5 h-4 w-4 rounded border-indigo-400 text-indigo-600 focus:ring-indigo-500"
                  data-testid="add-loc-create-admin-checkbox"
                />
                <span className="text-sm leading-snug">
                  <span className="font-semibold text-indigo-900">Also create initial admin user</span>
                  <span className="block text-xs text-indigo-800 mt-0.5">
                    Optional. Saves you a trip to the Users tab. Default role: <strong>admin</strong>.
                  </span>
                </span>
              </label>

              {form.create_admin && (
                <div className="space-y-3 pt-1">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">First Name *</Label>
                      <Input
                        value={form.admin_first_name}
                        onChange={(e) => setForm(f => ({ ...f, admin_first_name: e.target.value }))}
                        placeholder="Jane"
                        data-testid="add-loc-admin-first"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Last Name *</Label>
                      <Input
                        value={form.admin_last_name}
                        onChange={(e) => setForm(f => ({ ...f, admin_last_name: e.target.value }))}
                        placeholder="Doe"
                        data-testid="add-loc-admin-last"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">Email *</Label>
                    <Input
                      type="email"
                      value={form.admin_email}
                      onChange={(e) => setForm(f => ({ ...f, admin_email: e.target.value }))}
                      placeholder="jane@k9resorts.com"
                      data-testid="add-loc-admin-email"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Temporary Password * (min 8 chars)</Label>
                      <Input
                        type="text"
                        value={form.admin_password}
                        onChange={(e) => setForm(f => ({ ...f, admin_password: e.target.value }))}
                        placeholder="TempPass123"
                        data-testid="add-loc-admin-password"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Role</Label>
                      <Select
                        value={form.admin_role}
                        onValueChange={(v) => setForm(f => ({ ...f, admin_role: v }))}
                      >
                        <SelectTrigger data-testid="add-loc-admin-role"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="admin">Admin</SelectItem>
                          <SelectItem value="manager">Manager</SelectItem>
                          <SelectItem value="agent">Agent</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!form.send_welcome_email}
                      onChange={(e) => setForm(f => ({ ...f, send_welcome_email: e.target.checked }))}
                      className="mt-0.5 h-4 w-4 rounded border-indigo-400 text-indigo-600 focus:ring-indigo-500"
                      data-testid="add-loc-send-welcome-email"
                    />
                    <span className="text-xs leading-snug text-indigo-900">
                      Send welcome email with credentials (from master admin Gmail)
                    </span>
                  </label>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)} disabled={adding}>Cancel</Button>
            <Button
              onClick={submitAdd}
              disabled={adding || !form.name.trim()}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="submit-add-location"
            >
              {adding ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Adding…</> : "Add Location"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {remaining > 0 && (
        <div className="text-xs text-gray-500 mt-2 px-1">
          {remaining} more location slot{remaining > 1 ? "s" : ""} available under this tenant's cap.
        </div>
      )}
    </div>
  );
}

// ── Location card with retroactive "Inherit Gingr" affordance ───────────
function LocationCardRow({ loc, tenantId, onChanged }) {
  const [inheritOpen, setInheritOpen] = useState(false);
  const [partitionId, setPartitionId] = useState("");
  const [inheriting, setInheriting] = useState(false);

  const submitInherit = async () => {
    const pid = parseInt(partitionId, 10);
    if (!pid || pid < 1) {
      toast.error("Enter a valid Gingr partition ID");
      return;
    }
    setInheriting(true);
    try {
      const res = await axios.post(
        `${API}/admin/tenants/${tenantId}/locations/${loc.id}/inherit-gingr`,
        { gingr_location_id: pid, gingr_location_name: loc.name },
      );
      toast.success(res.data.message);
      setInheritOpen(false);
      setPartitionId("");
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to inherit Gingr");
    } finally {
      setInheriting(false);
    }
  };

  return (
    <div
      className="bg-white border rounded-lg p-3 flex items-center justify-between flex-wrap gap-3"
      data-testid={`location-card-${loc.id}`}
    >
      <div className="flex items-center gap-3">
        <div className="h-8 w-8 rounded-lg bg-indigo-100 flex items-center justify-center">
          <Building2 className="h-4 w-4 text-indigo-600" />
        </div>
        <div>
          <div className="font-semibold text-gray-900">{loc.name}</div>
          <div className="text-xs text-gray-500">
            {loc.city || "—"}{loc.state && `, ${loc.state}`}
            {loc.id && <span className="ml-2 text-gray-400">· {loc.id.slice(0, 8)}…</span>}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        {loc.has_gingr ? (
          <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">
            <ShieldCheck className="h-3 w-3 mr-1" /> Gingr OK
          </Badge>
        ) : (
          <Button
            size="sm"
            onClick={() => setInheritOpen(true)}
            className="h-7 text-xs bg-amber-500 hover:bg-amber-600 text-white"
            data-testid={`inherit-gingr-${loc.id}`}
          >
            Inherit Gingr API
          </Button>
        )}
        <a
          href={`/location-management?location=${loc.id}`}
          onClick={e => e.stopPropagation()}
        >
          <Button size="sm" variant="outline" className="h-7 text-xs">
            <Eye className="h-3.5 w-3.5 mr-1" /> Open
          </Button>
        </a>
      </div>

      <Dialog open={inheritOpen} onOpenChange={setInheritOpen}>
        <DialogContent className="max-w-md" data-testid="inherit-gingr-dialog">
          <DialogHeader>
            <DialogTitle>Inherit Gingr API for {loc.name}</DialogTitle>
            <DialogDescription>
              Apply the tenant's existing Gingr api_key + subdomain to this location.
              Specify the Gingr partition ID for this resort (an integer assigned by Gingr).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs">Gingr Partition ID *</Label>
              <Input
                type="number"
                min={1}
                value={partitionId}
                onChange={e => setPartitionId(e.target.value)}
                placeholder="e.g. 2"
                data-testid="inherit-partition-input"
              />
              <div className="text-[11px] text-gray-500 mt-1">
                You can find this in Gingr's admin panel — each resort has its own location ID.
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInheritOpen(false)} disabled={inheriting}>Cancel</Button>
            <Button
              onClick={submitInherit}
              disabled={inheriting || !partitionId}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="submit-inherit-gingr"
            >
              {inheriting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Applying…</> : "Inherit"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
